import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';

import { Button, LogoBloomays, LoaderGlobal, HrefLink } from '@bloomays-lib/ui.shared';
import { useAuthStore } from '@bloomays-lib/common.auth/browser';
import mainsIllustration from '../../assets/illustrations/mains.jpg';
import { Logger } from '../../services/service-logger';
import { TalentBoardEventType } from '@bloomays-lib/types.shared';

const logger = Logger('Home');

const Home = (): React.ReactNode => {
  const navigate = useNavigate();
  const { t } = useTranslation(['talentBoard']);
  const auth = useAuthStore((state) => state.auth);

  useEffect(() => {
    if (auth?.isAuthenticated) {
      logger.info('Traffic', { page: 'Home', evt: { name: TalentBoardEventType.TalentboardListJobs } });
      void navigate('/talentboard/jobs', { replace: true });
    }
  }, [auth?.isAuthenticated, navigate]);

  if (auth?.isAuthenticated) {
    return <LoaderGlobal waitingText="Redirection..." />;
  }

  return (
    <PageWrapper>
      <LogoContainer>
        <LogoBloomays alt="Bloomays" />
      </LogoContainer>
      <LoginContent>
        <LoginImage>
          <LogoContainerMobile>
            <LogoBloomays alt="Bloomays" />
          </LogoContainerMobile>
        </LoginImage>
        <LoginPage>
          <LoginBox>
            <WelcomeTitle>{t('homeTitle')}</WelcomeTitle>
            <DescriptionText>{t('homeDescription')}</DescriptionText>
            <ButtonsWrapper>
              <ButtonSection>
                <ButtonTitle component="div">
                  <div dangerouslySetInnerHTML={{ __html: t('accessClient') }} />
                </ButtonTitle>
                <ActionButton
                  textButton={t('requestMagicLinkButton')}
                  onClick={() => {
                    logger.info('Traffic', {
                      page: 'Home',
                      evt: { name: TalentBoardEventType.TalentboardGoToAskMagicLink },
                    });
                    void navigate('/sendMagicLink');
                  }}
                  emoji={<EmailOutlinedIcon />}
                  color="primary"
                  size="large"
                />
              </ButtonSection>
              <ButtonSection>
                <ButtonTitle component="div">
                  <div dangerouslySetInnerHTML={{ __html: t('accessTAM') }} />
                </ButtonTitle>
                <ActionButton
                  textButton={t('adminAcces')}
                  onClick={() => {
                    auth?.loginWithRedirect({
                      appState: { returnTo: '/talentboard/jobs' },
                    });
                  }}
                  emoji={<WorkOutlineIcon />}
                  color="primary"
                  size="large"
                />
              </ButtonSection>
            </ButtonsWrapper>
            <HrefLink
              text="Préférences de cookies"
              onClick={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
                e.preventDefault();
                (window as any).Cookiebot?.show();
              }}
              href="#"
            />
          </LoginBox>
        </LoginPage>
      </LoginContent>
    </PageWrapper>
  );
};

export default Home;
export { Home };

const PageWrapper = styled('div')({
  display: 'flex',
  width: '100%',
  minHeight: '100vh',
  alignItems: 'flex-start',
  background: '#FFF',
  position: 'relative',
});

const LogoContainer = styled('div')(({ theme }) => ({
  position: 'absolute',
  left: '52px',
  top: '40px',
  width: '213.872px',
  height: '31px',
  zIndex: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const LogoContainerMobile = styled('div')(({ theme }) => ({
  display: 'none',

  [theme.breakpoints.down('md')]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    top: '20%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '160px',
    height: '31px',
    zIndex: 20,
  },
}));

const LoginContent = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flex: 1,
  alignSelf: 'stretch',
  background: '#3B7FF5',
  minHeight: '100vh',
  position: 'relative',

  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    paddingTop: 0,
    alignItems: 'stretch',
    position: 'relative',
  },
}));

const LoginImage = styled('div')(({ theme }) => ({
  display: 'flex',
  width: '800px',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-end',
  alignSelf: 'stretch',
  background: `url("${mainsIllustration}") lightgray 50% / cover no-repeat`,
  position: 'relative',

  '&::after': {
    content: '""',
    width: '72px',
    height: '100%',
    background: 'linear-gradient(270deg, #3B7FF5 0%, rgba(59, 127, 245, 0.00) 77.4%)',
    position: 'absolute',
    right: 0,
    top: 0,
  },

  [theme.breakpoints.down('xl')]: {
    width: '40%',
  },

  [theme.breakpoints.down('md')]: {
    width: '100%',
    minWidth: '400px',
    height: '300px',
    alignSelf: 'stretch',
    position: 'relative',

    '&::after': {
      background: 'linear-gradient(180deg, rgba(59, 127, 245, 0.00) 0%, #3B7FF5 40%)',
      width: '100%',
      height: '50px',
      bottom: 0,
      top: 'auto',
      right: 'auto',
    },
  },

  [theme.breakpoints.down('sm')]: {
    height: '200px',
    order: 1,
    flexShrink: 0,
    position: 'relative',

    '&::after': {
      background: 'linear-gradient(180deg, rgba(59, 127, 245, 0.00) 0%, #3B7FF5 40%)',
      width: '100%',
      height: '40px',
      bottom: 0,
      top: 'auto',
      right: 'auto',
    },
  },
}));

const LoginPage = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '24px',
  flex: 1,
  padding: '20px',
  minHeight: '463px',

  [theme.breakpoints.down('md')]: {
    padding: '40px 20px',
    minHeight: 'auto',
  },

  [theme.breakpoints.down('sm')]: {
    padding: '20px 16px',
    order: 2,
    flex: 'none',
    width: '100%',
  },
}));

const LoginBox = styled('div')(({ theme }) => ({
  display: 'flex',
  maxWidth: '600px',
  width: '100%',
  padding: '24px',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '24px',
  borderRadius: '15px',
  background: '#FFF',

  [theme.breakpoints.down('md')]: {
    maxWidth: '500px',
    padding: '32px 24px',
  },

  [theme.breakpoints.down('sm')]: {
    padding: '24px 20px',
    gap: '20px',
  },
}));

const WelcomeTitle = styled('h1')(({ theme }) => ({
  alignSelf: 'stretch',
  color: '#000',
  textAlign: 'center',
  fontFamily: '"Instrument Serif Regular", serif',
  fontSize: '48px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '48px',
  textTransform: 'uppercase',
  margin: 0,

  [theme.breakpoints.down('md')]: {
    fontSize: '40px',
    lineHeight: '44px',
  },

  [theme.breakpoints.down('sm')]: {
    fontSize: '32px',
    lineHeight: '36px',
  },
}));

const DescriptionText = styled('p')(({ theme }) => ({
  alignSelf: 'stretch',
  color: '#000',
  fontFamily: '"Poppins Regular", sans-serif',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '30px',
  textAlign: 'center',
  margin: 0,

  [theme.breakpoints.down('sm')]: {
    fontSize: '13px',
    lineHeight: '18.2px',
  },
}));

const ButtonsWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '16px',
  alignSelf: 'stretch',
  width: '100%',

  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));

const ButtonSection = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  flex: 1,
  width: '100%',
  alignItems: 'center',

  [theme.breakpoints.down('sm')]: {
    alignItems: 'flex-start',
  },
}));

const ButtonTitle = styled(Typography)(() => ({
  fontSize: '16px',
}));

const ActionButton = styled(Button)(({ theme }) => ({
  width: '100%',
  height: '80px',
  justifyContent: 'center',
  borderRadius: '10px',
  minWidth: 0,

  [theme.breakpoints.down('sm')]: {
    width: '100%',
    height: '44px',
  },
}));
